import Cookie from '../services/cookie';

/**
 * Accessibility class to handle theme and text-size toggles with cookie persistence.
 * 
 * - Applies classes to <body> to reflect user preference.
 * - Persists preference using cookies for 7 days.
 * - Controls `.active` state on UI toggle elements.
 */
class Accessibility {
	constructor() {
		/**
		 * CSS selectors for theme toggle buttons.
		 * @type {string[]}
		 */
		this.themeToggleClasses = [
			'.js-accessibility__panel__toggle__light-toggle',
			'.js-accessibility__panel__toggle__dark-toggle'
		];

		/**
		 * CSS selectors for text size toggle buttons.
		 * @type {string[]}
		 */
		this.textToggleClasses = [
			'.js-accessibility__panel__toggle__default-text-toggle',
			'.js-accessibility__panel__toggle__text-toggle'
		];

		this.applyInitialState();
		this.setupEventListeners();
	}

	/**
	 * Applies initial settings for theme and text size based on cookie values.
	 * If no cookie is set, defaults to 'light' theme and 'default' text size.
	 */
	applyInitialState() {
		const theme = Cookie.get('accessibilityTheme') || 'light';
		const textSize = Cookie.get('accessibilityText') || 'default';

		this.applySetting('accessibilityTheme', theme);
		this.applySetting('accessibilityText', textSize);
	}

	/**
	 * Stores the accessibility setting in a cookie and applies it.
	 *
	 * @param {string} type - Either 'accessibilityTheme' or 'accessibilityText'.
	 * @param {string} value - The value to store and apply, e.g. 'dark', 'large'.
	 */
	storeAccessibilitySetting(type, value) {
		Cookie.set(type, value, 7); // 7-day expiry
		this.applySetting(type, value);
	}

	/**
	 * Applies the selected accessibility setting by:
	 * - Updating <body> classes.
	 * - Updating active toggle UI classes.
	 *
	 * @param {string} type - 'accessibilityTheme' or 'accessibilityText'.
	 * @param {string} value - Value like 'light', 'dark', 'default', or 'large'.
	 */
	applySetting(type, value) {
		if (type === 'accessibilityTheme') {
			document.body.classList.remove('theme-light', 'theme-dark');
			document.body.classList.add(`theme-${value}`);
			this.setActiveToggle(this.themeToggleClasses, `.js-accessibility__panel__toggle__${value}-toggle`);
		}

		if (type === 'accessibilityText') {
			document.body.classList.remove('text-default', 'text-large');
			document.body.classList.add(`text-${value}`);

			const toggleSelector =
				value === 'large'
					? '.js-accessibility__panel__toggle__text-toggle'
					: '.js-accessibility__panel__toggle__default-text-toggle';

			this.setActiveToggle(this.textToggleClasses, toggleSelector);
		}
	}

	/**
	 * Toggles `.active` class on the selected toggle and removes it from the rest.
	 *
	 * @param {string[]} allToggleSelectors - Array of selectors in the toggle group.
	 * @param {string} activeSelector - Selector to receive the `.active` class.
	 */
	setActiveToggle(allToggleSelectors, activeSelector) {
		$(allToggleSelectors.join(',')).removeClass('active');
		$(activeSelector).addClass('active');
	}

	/**
	 * Binds event listeners for toggles and the panel open/close button.
	 */
	setupEventListeners() {
		// Toggle accessibility panel visibility
		$(document).on('click', '.js-accessibility__toggle', function (e) {
			const $toggle = $(this);
			const $panel = $toggle.closest('.js-accessibility');
			const isExpanded = $panel.toggleClass('active').hasClass('active');
			$toggle.attr('aria-expanded', isExpanded ? 'true' : 'false');

			e.stopPropagation();
		});

		// Close panel when clicking outside
		$(document).on('click', function (e) {
			const $target = $(e.target);
			if (!$target.closest('.js-accessibility').length) {
				$('.js-accessibility').removeClass('active');
				$('.js-accessibility__toggle').attr('aria-expanded', 'false');
			}
		});

		// Theme toggle events
		$(document).on('click', '.js-accessibility__panel__toggle__light-toggle', (e) => {
			e.preventDefault();
			this.storeAccessibilitySetting('accessibilityTheme', 'light');
		});

		$(document).on('click', '.js-accessibility__panel__toggle__dark-toggle', (e) => {
			e.preventDefault();
			this.storeAccessibilitySetting('accessibilityTheme', 'dark');
		});

		// Text size toggle events
		$(document).on('click', '.js-accessibility__panel__toggle__default-text-toggle', (e) => {
			e.preventDefault();
			this.storeAccessibilitySetting('accessibilityText', 'default');
		});

		$(document).on('click', '.js-accessibility__panel__toggle__text-toggle', (e) => {
			e.preventDefault();
			this.storeAccessibilitySetting('accessibilityText', 'large');
		});
	}
}

export default Accessibility;